import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import BaseIngredientCategory from 'classes/StandingData/BaseIngredientCategory/Search/BaseIngredientCategory';
import BaseIngredientCategoryList from 'classes/StandingData/BaseIngredientCategory/Search/BaseIngredientCategoryList';
import Search from 'classes/StandingData/BaseIngredientCategory/Search/Search';
import BaseIngredientCategoryDetailComponent from 'components/desktop/StandingData/BaseIngredientCategory/Detail/BaseIngredientCategoryDetailComponent';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import StandingDataList, {
	Row,
} from 'components/desktop/_general/StandingDataList/StandingDataList';
import clone from 'functions/clone';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { usePermissions } from 'hooks/usePermissions';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 5;

const BaseIngredientCategoryManagementComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isAdministrator } = usePermissions();
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [displayOverlay, setDisplayOverlay] = useState<boolean>(false);

	const [baseIngredientCategoryList, setBaseIngredientCategoryList] =
		useState<BaseIngredientCategoryList>(new BaseIngredientCategoryList());
	const [search, setSearch] = useState<Search>(new Search());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (props.archive !== undefined && props.archive == true) {
			search.showOnlyDeleted = true;
		}
		if (window.location.href.includes('?')) {
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			if (search.id.value) setDisplayOverlay(true);
			await getBaseIngredientCategories();
		} else {
			await getBaseIngredientCategories();
		}
		setSearch(clone(search));
	};

	const getBaseIngredientCategories = async () => {
		setIsLoading(true);
		const response = await baseIngredientCategoryList.callApi(search.mapToRequest());
		if (response.hasValue()) {
			baseIngredientCategoryList.mapFromApi(response.get());
			setBaseIngredientCategoryList(clone(baseIngredientCategoryList));
		}
		setIsLoading(false);
	};

	const getSuggestedBaseIngredientCategories = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await baseIngredientCategoryList.search(searchTerm, props.archive ?? false);
		} else {
			baseIngredientCategoryList.filtered = [];
		}

		setBaseIngredientCategoryList(clone(baseIngredientCategoryList));
		return baseIngredientCategoryList.filtered;
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getBaseIngredientCategories();
	};

	const handleSearch = (input?: string) => {
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const handleChangeId = (id: string | null) => {
		search.id.value = id;
		handleChangeUrl();
	};

	const handleOverlayOpen = (input: string): void => {
		setDisplayOverlay(true);
		handleChangeId(input);
	};

	const handleOverlayClose = (): void => {
		setDisplayOverlay(false);
		handleChangeId(null);
	};

	const handleArchiveBaseIngredientCategory = async (
		baseIngredientCategory: BaseIngredientCategory
	): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			cancelButtonText: t('_general:CANCEL'),
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			icon: 'warning',
			iconColor: '#ff0004',
			showCancelButton: true,
			text: t('standingData:BASEINGREDIENTCATEGORY_ARCHIVE_DIALOG'),
			title: t('standingData:ARCHIVE_TITLE'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await baseIngredientCategory.callApiDelete();
				await Swal.fire({
					confirmButtonColor: '#00cc07',
					title: t('standingData:BASEINGREDIENTCATEGORY_ARCHIVE_CONFIRMATION'),
				});
				await getBaseIngredientCategories();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleRestoreBaseIngredientCategory = async (
		baseIngredientCategory: BaseIngredientCategory
	): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			cancelButtonText: t('_general:CANCEL'),
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			icon: 'warning',
			iconColor: '#00cc07',
			showCancelButton: true,
			text: t('standingData:BASEINGREDIENTCATEGORY_RESTORE_DIALOG'),
			title: t('standingData:RESTORE_TITLE'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await baseIngredientCategory.callApiRestore();
				await Swal.fire({
					confirmButtonColor: '#00cc07',
					title: t('standingData:BASEINGREDIENTCATEGORY_RESTORE_CONFIRMATION'),
				});
				await getBaseIngredientCategories();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleChangeIdToNew = (): void => {
		handleChangeId('new');
		setDisplayOverlay(true);
	};

	const getButtonDataArchive = (input: BaseIngredientCategory): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleArchiveBaseIngredientCategory(input),
			};
		}
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleOverlayOpen(id),
			};
		}
	};

	const getButtonDataRestore = (input: BaseIngredientCategory): BtnData | undefined => {
		if (props.archive) {
			return {
				onClick: () => handleRestoreBaseIngredientCategory(input),
			};
		}
	};

	const renderButtons = (input: BaseIngredientCategory): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit(input.id)}
				archive={getButtonDataArchive(input)}
				restore={getButtonDataRestore(input)}
			/>
		);
	};

	const renderListRows = (): Row[] => {
		return baseIngredientCategoryList.all.map(
			(baseIngredientCategory: BaseIngredientCategory) => {
				return {
					id: baseIngredientCategory.id,
					jsx: (
						<div
							className="d-flex flex-row justify-content-start align-items-center"
							style={{ gap: '2em' }}
						>
							<div className="d-flex flex-row" style={{ width: '300px' }}>
								{baseIngredientCategory.name}
							</div>
							{baseIngredientCategory.parentName ? (
								<>
									<div style={{ width: '300px' }}>
										<span className="grey">
											{t('_general:NUTRI_SCORE_CATEGORY')}:{' '}
										</span>
										{baseIngredientCategory.nutriScoreCategory}
									</div>
									<div className="flex-fill">
										<span className="grey">{t('standingData:PARENT')}: </span>
										{baseIngredientCategory.parentName}
									</div>
								</>
							) : (
								<div className="flex-fill">
									<span className="grey">
										{t('_general:NUTRI_SCORE_CATEGORY')}:{' '}
									</span>
									{baseIngredientCategory.nutriScoreCategory}
								</div>
							)}
							<div>{renderButtons(baseIngredientCategory)}</div>
						</div>
					),
				};
			}
		);
	};

	const renderContent = () => {
		return (
			<>
				<main className="container">
					<div className="d-flex flex-row justify-content-between">
						{!props.archive ? (
							<>
								<h1>{t('standingData:BASEINGREDIENTCATEGORY_MANAGEMENT')}</h1>
								{isAdministrator && (
									<div className="d-flex flex-gap-25">
										<BtnNew
											onClick={handleChangeIdToNew}
											name={t('standingData:CREATE_BASEINGREDIENTCATEGORY')}
										/>
									</div>
								)}
							</>
						) : (
							<h1>{t('standingData:BASEINGREDIENTCATEGORY_ARCHIVE')}</h1>
						)}
					</div>
					<br />
					<div className="row">
						<div className="col-lg-12">
							<div className="input-group mb-3">
								<AdvancedSearchInput
									debounceMilliSeconds={350}
									getSuggestedItems={getSuggestedBaseIngredientCategories}
									handleSearch={handleSearch}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									triggerSearchLetterAmount={1}
								/>
							</div>
						</div>
					</div>
					<br />
					<LoadingAnimation isLoading={isLoading} />
					<p>
						{baseIngredientCategoryList.totalCount +
							' ' +
							t('_general:BASEINGREDIENTCATEGORIES')}
					</p>
					<StandingDataList
						displayOverlay={displayOverlay}
						rows={renderListRows()}
						overlay={<BaseIngredientCategoryDetailComponent />}
						handleOverlayOpen={handleOverlayOpen}
						handleOverlayClose={handleOverlayClose}
					/>
					<br />
					<Pagination
						countOfAllListItems={baseIngredientCategoryList.totalCount}
						pageIndex={search.getPageIndex()}
						setPageIndex={handleChangePageIndex}
						listItemAmount={search.getPageSize()}
						maxNumberOfButtons={maxNumberOfButtons}
					/>
				</main>
			</>
		);
	};

	return <>{renderContent()}</>;
};

export default BaseIngredientCategoryManagementComponent;
